import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { IonButton, IonContent, IonIcon } from '@ionic/angular/standalone';
import { PageRefresherComponent } from '../../shared/page-refresher/page-refresher.component';
import { SiteFooterComponent } from '../../shared/site-footer/site-footer.component';
import { SiteHeaderComponent } from '../../shared/site-header/site-header.component';

interface GuideVisual {
  title: string;
  url: string;
  menu: string[];
  screenshot?: string;
  fields?: string[];
  table?: string[];
  note?: string;
}

interface GuideSection {
  id: string;
  eyebrow: string;
  title: string;
  intro: string;
  route: string;
  navigation: string[];
  steps: string[];
  outcome: string[];
  visual: GuideVisual;
}

interface GuideNavGroup {
  title: string;
  links: Array<{
    id: string;
    title: string;
  }>;
}

@Component({
  selector: 'app-backend-guide',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, IonButton, IonContent, IonIcon, PageRefresherComponent, SiteHeaderComponent, SiteFooterComponent],
  templateUrl: './backend-guide.page.html',
  styleUrls: ['./backend-guide.page.scss'],
})
export class BackendGuidePage {
  @ViewChild(IonContent) private content?: IonContent;

  backendUrl = 'https://backend.safarizine.com/backend';
  activeSectionId = 'login';
  guideSearch = '';
  isGuideMenuOpen = false;
  previewScreenshot = '';
  previewTitle = '';

  quickStats = [
    { value: '12+', label: 'Backend modules' },
    { value: 'No code', label: 'Content editing' },
    { value: 'Live API', label: 'Frontend updates' },
  ];

  navGroups: GuideNavGroup[] = [
    {
      title: 'Getting Started',
      links: [
        { id: 'login', title: 'Login to the backend' },
        { id: 'dashboard', title: 'Dashboard menu' },
      ],
    },
    {
      title: 'Website Content',
      links: [
        { id: 'basic-details', title: 'Basic Details' },
        { id: 'content', title: 'Content' },
        { id: 'amenities', title: 'Amenities' },
        { id: 'offers', title: 'Special Offers' },
        { id: 'gallery', title: 'Gallery' },
        { id: 'how-to-reach', title: 'How to reach' },
      ],
    },
    {
      title: 'Customers',
      links: [
        { id: 'enquiry', title: 'Enquiry' },
        { id: 'users', title: 'User' },
      ],
    },
    {
      title: 'Administration',
      links: [
        { id: 'pages', title: 'Page' },
        { id: 'email-template', title: 'Email template' },
        { id: 'settings', title: 'Website settings' },
        { id: 'acl', title: 'ACL permissions' },
      ],
    },
  ];

  sections: GuideSection[] = [
    {
      id: 'login',
      eyebrow: 'Step 01',
      title: 'Login to the backend',
      intro: 'Customers can open the admin panel from the demo backend URL and sign in to manage the lodge website.',
      route: '/backend/auth',
      navigation: ['Open Demo Backend', 'Login'],
      steps: [
        'Open the backend URL in a browser.',
        'Enter the admin username and password.',
        'Click Sign in to open the dashboard.',
      ],
      outcome: [
        'Secure admin access for staff.',
        'Dashboard opens after successful login.',
      ],
      visual: {
        title: 'Get Access',
        url: '/backend/auth',
        screenshot: 'assets/images/backend-guide/01-login.png',
        menu: ['Username', 'Password', 'Sign in'],
        fields: ['Username *', 'Password *'],
        note: 'Login screen with Safarizine branding.',
      },
    },
    {
      id: 'dashboard',
      eyebrow: 'Step 02',
      title: 'Understand the dashboard menu',
      intro: 'The left sidebar gives quick access to the main content and operation modules.',
      route: '/backend/dashboard',
      navigation: ['Login', 'Dashboard'],
      steps: [
        'After login, review the sidebar modules.',
        'Use Basic Details, Content, Amenities, Offers, Gallery, and How to reach for website content.',
        'Use Enquiry, User, Page, Email template, Settings, and ACL for operations.',
      ],
      outcome: [
        'Admin users see only allowed menu items.',
        'ACL can control module visibility and permissions.',
      ],
      visual: {
        title: 'Dashboard',
        url: '/backend/dashboard',
        screenshot: 'assets/images/backend-guide/02-dashboard.png',
        menu: ['Dashboard', 'Basic Details', 'Content', 'Amenities', 'Special Offers', 'Gallery', 'How to reach', 'Enquiry', 'User', 'Page', 'Email template'],
        note: 'Welcome screen and module navigation.',
      },
    },
    {
      id: 'basic-details',
      eyebrow: 'Lodge setup',
      title: 'Change lodge name and video URL',
      intro: 'Basic Details controls the main lodge identity used by the frontend.',
      route: '/backend/listing/settings?form_tab=basic',
      navigation: ['Dashboard', 'Basic Details'],
      steps: [
        'Click Basic Details in the sidebar.',
        'Update the Name field.',
        'Add the video URL when needed.',
        'Click Update to save changes.',
      ],
      outcome: [
        'Lodge name updates in frontend areas that use listing details.',
        'Video content can be connected to the website experience section.',
      ],
      visual: {
        title: 'Update Setting : Safarizine',
        url: '/backend/listing/settings?form_tab=basic',
        screenshot: 'assets/images/backend-guide/03-basic-details.png',
        menu: ['Basic Details', 'Name', 'Video URL', 'Update'],
        fields: ['Name *', 'Video URL'],
        note: 'Primary lodge settings form.',
      },
    },
    {
      id: 'content',
      eyebrow: 'Website copy',
      title: 'Edit intro, brief info, and overview',
      intro: 'The Content tab lets the customer update homepage and about-style website copy with rich text editors.',
      route: '/backend/listing/settings?form_tab=page_design',
      navigation: ['Dashboard', 'Content'],
      steps: [
        'Click Content in the sidebar.',
        'Update Small Intro for short marketing text.',
        'Update Brief info for summary content.',
        'Update Overview for long-form lodge description.',
        'Click Update to publish the changes.',
      ],
      outcome: [
        'Marketing text changes without a developer.',
        'Rich text formatting can be used for longer sections.',
      ],
      visual: {
        title: 'Update Setting : Safarizine',
        url: '/backend/listing/settings?form_tab=page_design',
        screenshot: 'assets/images/backend-guide/04-content.png',
        menu: ['Content', 'Small Intro', 'Brief info', 'Overview'],
        fields: ['Small Intro *', 'Brief info editor', 'Overview editor'],
        note: 'Rich text content management.',
      },
    },
    {
      id: 'amenities',
      eyebrow: 'Amenities',
      title: 'Add or update lodge amenities',
      intro: 'Amenities make it easy to show facilities and services such as dining, pickup support, and safari guidance.',
      route: '/backend/listing/settings?form_tab=amenities',
      navigation: ['Dashboard', 'Amenities'],
      steps: [
        'Click Amenities in the sidebar.',
        'Edit Amenity Title and Short Info for existing items.',
        'Click Add Amenity to create a new item.',
        'Click Remove to delete an amenity row.',
        'Click Update after changes.',
      ],
      outcome: [
        'Amenities display on the frontend Amenities page.',
        'Customers can quickly change facility messaging.',
      ],
      visual: {
        title: 'Amenities',
        url: '/backend/listing/settings?form_tab=amenities',
        screenshot: 'assets/images/backend-guide/05-amenities.png',
        menu: ['Amenities', 'Add Amenity', 'Remove', 'Update'],
        fields: ['Amenity Title *', 'Short Info'],
        note: 'Repeatable amenity rows.',
      },
    },
    {
      id: 'offers',
      eyebrow: 'Promotions',
      title: 'Manage special offers',
      intro: 'Special Offers supports promotional packages with title, details, dates, media, and exclusive flags.',
      route: '/backend/listing/settings?form_tab=offers',
      navigation: ['Dashboard', 'Special Offers'],
      steps: [
        'Click Special Offers in the sidebar.',
        'Click Click to Add Offer to create a new offer.',
        'Use the edit icon to update an existing offer.',
        'Use the delete icon to remove expired or unwanted offers.',
        'Search offers from the table search field.',
      ],
      outcome: [
        'Offers appear on the frontend Offers page.',
        'Offer validity dates and images help create purchase urgency.',
      ],
      visual: {
        title: 'Special Offers',
        url: '/backend/listing/settings?form_tab=offers',
        screenshot: 'assets/images/backend-guide/06-special-offers.png',
        menu: ['Special Offers', 'Click to Add Offer', 'Search', 'Actions'],
        table: ['Title', 'Details', 'Valid From', 'Valid To', 'Media', 'Exclusive', 'Actions'],
        note: 'Offer table with edit and delete actions.',
      },
    },
    {
      id: 'gallery',
      eyebrow: 'Media library',
      title: 'Upload and organize gallery images',
      intro: 'Gallery management lets customers upload lodge photos, add title/credit details, and reorder the display.',
      route: '/backend/listing/settings?form_tab=image',
      navigation: ['Dashboard', 'Gallery'],
      steps: [
        'Click Gallery in the sidebar.',
        'Choose one or more image files.',
        'Use landscape images around 1600x1200px or higher.',
        'Drag images to set the view order.',
        'Add optional title and credit.',
        'Use View or Delete for each image.',
      ],
      outcome: [
        'Frontend gallery refreshes from backend image data.',
        'Large images are optimized by the backend.',
      ],
      visual: {
        title: 'Upload Image files',
        url: '/backend/listing/settings?form_tab=image',
        screenshot: 'assets/images/backend-guide/07-gallery.png',
        menu: ['Gallery', 'Choose files', 'Drag order', 'Title', 'Credit'],
        fields: ['Choose files', 'Title', 'Credit', 'View', 'Delete'],
        note: 'Image upload and ordering grid.',
      },
    },
    {
      id: 'how-to-reach',
      eyebrow: 'Location content',
      title: 'Update how-to-reach details',
      intro: 'This module controls travel guidance, pickup notes, landmarks, and guest arrival instructions.',
      route: '/backend/listing/settings?form_tab=howtoreach',
      navigation: ['Dashboard', 'How to reach'],
      steps: [
        'Click How to reach in the sidebar.',
        'Update the Address rich text field.',
        'Add arrival notes, pickup information, and nearby reference points.',
        'Click Update.',
      ],
      outcome: [
        'Guests can understand arrival and transfer details before booking.',
        'Operations teams can update travel guidance anytime.',
      ],
      visual: {
        title: 'How to reach',
        url: '/backend/listing/settings?form_tab=howtoreach',
        screenshot: 'assets/images/backend-guide/08-how-to-reach.png',
        menu: ['How to reach', 'Address editor', 'Update'],
        fields: ['Address *'],
        note: 'Travel guidance editor.',
      },
    },
    {
      id: 'enquiry',
      eyebrow: 'Lead management',
      title: 'Review customer enquiries',
      intro: 'The Enquiry module helps staff review and respond to customer messages submitted from the frontend.',
      route: '/backend/enquiry',
      navigation: ['Dashboard', 'Enquiry'],
      steps: [
        'Click Enquiry in the sidebar.',
        'Filter by replied or unreplied records.',
        'Search by name, email, or message.',
        'Use row actions to view, reply, change status, or delete when allowed.',
      ],
      outcome: [
        'Website enquiries are collected in one backend table.',
        'Staff can track which enquiries are replied.',
      ],
      visual: {
        title: 'Manage Enquiry',
        url: '/backend/enquiry',
        screenshot: 'assets/images/backend-guide/09-enquiry.png',
        menu: ['Enquiry', 'Filter', 'Search', 'Actions'],
        table: ['Name', 'Email', 'Message', 'Is replied?', 'Datetime', 'Actions'],
        note: 'Lead and contact enquiry management.',
      },
    },
    {
      id: 'users',
      eyebrow: 'Users',
      title: 'Manage website users',
      intro: 'The User module provides account controls for registered users.',
      route: '/backend/user',
      navigation: ['Dashboard', 'User'],
      steps: [
        'Click User in the sidebar.',
        'Use date and status filters.',
        'Click Add New to create a user.',
        'Use row actions to view, edit, lock/unlock, change status, or delete.',
      ],
      outcome: [
        'Admin can manage customer account status.',
        'User lists can be searched and exported when needed.',
      ],
      visual: {
        title: 'Manage User',
        url: '/backend/user',
        screenshot: 'assets/images/backend-guide/10-user.png',
        menu: ['User', 'Add New', 'Active', 'Inactive', 'Search'],
        table: ['Name', 'Email', 'Mobile', 'Last Login', 'Status', 'Actions'],
        note: 'User account table.',
      },
    },
    {
      id: 'pages',
      eyebrow: 'Static pages',
      title: 'Edit Privacy Policy and Terms pages',
      intro: 'The Page module manages static website pages required by the frontend.',
      route: '/backend/page',
      navigation: ['Dashboard', 'Page'],
      steps: [
        'Click Page in the sidebar.',
        'Click Add New to create a page.',
        'Use the edit icon to update Privacy Policy or Terms & Conditions.',
        'Keep required page slugs matched with frontend routes.',
        'Use Active or Inactive to control visibility.',
      ],
      outcome: [
        'Policy pages are editable from backend.',
        'Frontend routes can display backend page content.',
      ],
      visual: {
        title: 'Manage Page',
        url: '/backend/page',
        screenshot: 'assets/images/backend-guide/11-page.png',
        menu: ['Page', 'Add New', 'Active', 'Inactive', 'Delete All'],
        table: ['Name', 'Full Width Content', 'Last Updated', 'Status', 'Actions'],
        note: 'Required pages include Privacy Policy and Terms & Conditions.',
      },
    },
    {
      id: 'email-template',
      eyebrow: 'Automation',
      title: 'Manage email templates',
      intro: 'Email templates control automatic messages for OTP, enquiry received, and admin replies.',
      route: '/backend/email_template',
      navigation: ['Dashboard', 'Email template'],
      steps: [
        'Click Email template in the sidebar.',
        'Click Add New if a new template is needed.',
        'Use edit to update subject and email body.',
        'Use view to preview the email.',
        'Keep placeholders such as {site_title} and {subject} intact.',
      ],
      outcome: [
        'Transactional emails remain brand-consistent.',
        'Admin can update templates without changing code.',
      ],
      visual: {
        title: 'Manage Email Template',
        url: '/backend/email_template',
        screenshot: 'assets/images/backend-guide/12-email-template.png',
        menu: ['Email template', 'Add New', 'View', 'Edit', 'Delete'],
        table: ['Name', 'Hint', 'Subject', 'Last Updated', 'Actions'],
        note: 'Template rows for OTP and enquiry emails.',
      },
    },
    {
      id: 'settings',
      eyebrow: 'Global settings',
      title: 'Change website settings and highlight notes',
      intro: 'Website Settings stores global values such as site title, date format, info email, scripts, metadata, and highlight notes.',
      route: '/backend/websitesetting',
      navigation: ['Admin Controls', 'Settings'],
      steps: [
        'Open Admin Controls from the top-right menu.',
        'Click Settings.',
        'Update the needed values.',
        'Edit Highlight notes to change the frontend demo notice.',
        'Click Save Settings.',
      ],
      outcome: [
        'Settings API provides app-wide settings to the frontend.',
        'Highlight notes refresh on app start and mobile pull-to-refresh.',
      ],
      visual: {
        title: 'Website settings',
        url: '/backend/websitesetting',
        screenshot: 'assets/images/backend-guide/13-website-settings.png',
        menu: ['Admin Controls', 'Settings', 'Save Settings'],
        fields: ['Site title *', 'Info email *', 'Site keywords', 'Site meta', 'Head scripts', 'Highlight notes'],
        note: 'Global settings affect website directly.',
      },
    },
    {
      id: 'acl',
      eyebrow: 'Permissions',
      title: 'Control backend menu permissions with ACL',
      intro: 'ACL is used to manage which backend modules each role can see and what actions they can perform.',
      route: '/backend/acl',
      navigation: ['Admin Controls', 'ACL'],
      steps: [
        'Open Admin Controls from the top-right menu.',
        'Click ACL.',
        'Select a role such as Super Admin or Moderator.',
        'Enable or disable Visible, Add, Edit, Delete, and Change Status permissions.',
        'Click Save Settings.',
      ],
      outcome: [
        'Role-based backend access for staff.',
        'Menus can be shown or hidden dynamically.',
      ],
      visual: {
        title: 'Module Permissions',
        url: '/backend/acl',
        screenshot: 'assets/images/backend-guide/14-acl.png',
        menu: ['Admin Controls', 'ACL', 'Super Admin', 'Moderator'],
        table: ['Controller', 'Visible', 'Add', 'Edit', 'Delete', 'Change Status'],
        note: 'ACL controls backend menu and module permissions.',
      },
    },
  ];

  supportSections = [
    {
      title: 'Edit Profile',
      screenshot: 'assets/images/backend-guide/15-profile.png',
      text: 'Admins can update first name, last name, email, username, and optional profile password from Admin Controls.',
    },
    {
      title: 'Change Password',
      screenshot: 'assets/images/backend-guide/16-change-password.png',
      text: 'Admins can change their password using old password, new password, and confirmation fields.',
    },
    {
      title: 'Sign Out',
      screenshot: '',
      text: 'Admins can safely sign out from the top-right Admin Controls menu.',
    },
  ];

  get activeSection() {
    return this.sections.find(section => section.id === this.activeSectionId) || this.sections[0];
  }

  get activeSectionIndex() {
    return Math.max(0, this.sections.findIndex(section => section.id === this.activeSectionId));
  }

  get previousSection() {
    return this.activeSectionIndex > 0 ? this.sections[this.activeSectionIndex - 1] : null;
  }

  get nextSection() {
    return this.activeSectionIndex < this.sections.length - 1 ? this.sections[this.activeSectionIndex + 1] : null;
  }

  get filteredNavGroups() {
    const term = this.guideSearch.trim().toLowerCase();

    if (!term) {
      return this.navGroups;
    }

    return this.navGroups
      .map(group => ({
        ...group,
        links: group.links.filter(link => link.title.toLowerCase().includes(term)),
      }))
      .filter(group => group.links.length);
  }

  selectSection(sectionId: string) {
    this.activeSectionId = sectionId;
    this.isGuideMenuOpen = false;
    this.content?.scrollToTop(250);
  }

  toggleGuideMenu() {
    this.isGuideMenuOpen = !this.isGuideMenuOpen;
  }

  hideMissingScreenshot(section: GuideSection) {
    section.visual.screenshot = '';
  }

  openScreenshotPreview(section: GuideSection) {
    if (!section.visual.screenshot) {
      return;
    }

    this.previewScreenshot = section.visual.screenshot;
    this.previewTitle = section.visual.title;
  }

  closeScreenshotPreview() {
    this.previewScreenshot = '';
    this.previewTitle = '';
  }
}
